import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  deleteDeviationItem,
  fetchDeviation,
  updateDeviation,
} from "../../../../api/quality/deviation";
import {
  dateToString,
  HtmlTextFormatter,
} from "../../../../components/Utils";
import {
  DeviationItemActionStatus,
  DeviationPriority,
  DeviationSeverity,
  DeviationStatus,
  deviationConsequenceOptions,
} from "../../../../components/dashboard/quality/deviations/utils";
import { DocumentViewer } from "../../../../components/dashboard/Media/documentViewer";
import SimplePrompt from "../../../../components/utils/simplePrompt";
import DeviationNewDialog from "../../../../components/dashboard/quality/deviations/deviationNewDialog";
import { DeviationPrintDialog } from "../../../../components/dashboard/quality/deviations/deviationPrint";
import { DeviationSendDialog } from "../../../../components/dashboard/quality/deviations/DeviationSendDialog";
import { DeviationItemEditorDialog } from "../../../../components/dashboard/quality/deviations/DeviationItemEditorDialog";
import { HistoryModuleView } from "../../../../components/dashboard/histories/module-view";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { WorkOrderSelector } from "../../../../components/utils/moduleSelectors/workOrderSelector";

const notificationLabels = {
  customer: "Kunde",
  contact: "Kontakt hos kunde",
  project_leader: "Prosjektleder",
  internal_project_leader: "Intern prosjektleder",
  custom: "Valgfri mottaker",
  sender_copy: "Kopi til avsender",
};

const getNotificationLabel = (key, value) => {
  if (notificationLabels[key]) return notificationLabels[key];
  if (key?.startsWith("project_contact_")) {
    return value?.recipient_name
      ? `Prosjektkontakt: ${value.recipient_name}`
      : "Prosjektkontakt";
  }
  return key;
};

const SectionCard = ({ title, description, action, children }) => (
  <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        )}
      </div>
      {action}
    </div>
    <div className="px-5 py-4">{children}</div>
  </section>
);

const NotificationStatusCard = ({ notificationStatuses = {} }) => {
  const entries = Object.entries(notificationStatuses || {});

  return (
    <SectionCard
      title="Varsling"
      description="Viser hvem som allerede har mottatt informasjon om avviket."
    >
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">Ingen varsler er sendt ennå.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {entries.map(([key, value]) => (
            <div
              key={key}
              className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700"
            >
              {getNotificationLabel(key, value)}
              {value?.sent_at ? ` · ${dateToString(value.sent_at)}` : ""}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const WorkOrderLinkCard = ({ deviation, onOpenWorkOrderDialog, t }) => {
  const workOrder = deviation?.work_order;

  return (
    <SectionCard
      title={t("deviation.work_order.section_title")}
      description="Arbeidsordren oppdaterer koblede data på avviket automatisk."
      action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {workOrder?.id ? (
            <>
              <Link
                to={`/dashboard/work_orders/${workOrder.id}`}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <WrenchScrewdriverIcon className="h-4 w-4" />
                {t("deviation.work_order.open_linked")}
              </Link>
              <button
                type="button"
                onClick={onOpenWorkOrderDialog}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                {t("deviation.work_order.change")}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onOpenWorkOrderDialog}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <WrenchScrewdriverIcon className="h-4 w-4" />
              {t("deviation.work_order.link_open")}
            </button>
          )}
        </div>
      }
    >
      {workOrder?.id ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Arbeidsordre
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {workOrder.displayTitle}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {workOrder.status || "–"}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Kontakt
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {workOrder.contact?.displayTitle || "–"}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Prosjektleder
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {workOrder.responsible_executor_participant?.user?.displayTitle ||
                workOrder.responsible_participant?.user?.displayTitle ||
                "–"}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          Dette avviket er ikke knyttet til en arbeidsordre ennå.
        </p>
      )}
    </SectionCard>
  );
};

export const DeviationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    deviation: {},
  });
  const [deviationDialogOpen, setDeviationDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [explanationDialogOpen, setExplanationDialogOpen] = useState(false);
  const [consequenceDialogOpen, setConsequenceDialogOpen] = useState(false);
  const [deviationItemDeletePromptOpen, setDeviationItemDeletePromptOpen] =
    useState(false);
  const [selectedDeviationItem, setSelectedDeviationItem] = useState(null);
  const [workOrderDialogOpen, setWorkOrderDialogOpen] = useState(false);
  const [workOrderPickerTab, setWorkOrderPickerTab] = useState("existing");

  const getDeviation = async () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      const data = await fetchDeviation(id);
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        deviation: data?.data,
      }));
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong when trying to load the deviation");
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        error: true,
      }));
    }
  };

  useEffect(() => {
    getDeviation();
  }, [id]);

  const handleOnRefresh = (data) => {
    if (data?.id) {
      setItemState((prevState) => ({
        ...prevState,
        deviation: data,
      }));
      return;
    }

    getDeviation();
  };

  const syncSavedItemIntoState = (savedItem, kind) => {
    setItemState((prevState) => {
      const deviation = prevState.deviation || {};
      const items = [...(deviation.items || [])];
      const existingIndex = items.findIndex((item) => item.id === savedItem.id);

      if (existingIndex >= 0) {
        items[existingIndex] = savedItem;
      } else {
        items.unshift(savedItem);
      }

      const nextDeviation = { ...deviation, items };

      if (kind === "explanation") {
        nextDeviation.latest_explanation_item = savedItem;
      }

      nextDeviation.consequence_items = items.filter(
        (item) =>
          item.title === "CONSEQUENCE" ||
          item.consequence
      );

      return {
        ...prevState,
        deviation: nextDeviation,
      };
    });
  };

  const promptDeleteDeviationItem = (item) => {
    setSelectedDeviationItem(item);
    setDeviationItemDeletePromptOpen(true);
  };

  const handleDeleteDeviationItem = async () => {
    const deviation = itemState.deviation;
    const payload = {
      deviation_id: deviation?.id,
      id: selectedDeviationItem?.id,
    };

    const response = await deleteDeviationItem(payload);

    if (response?.status === 200) {
      toast.success("Innlegget ble slettet");
      setItemState((prevState) => {
        const nextItems = (prevState.deviation?.items || []).filter(
          (item) => item.id !== selectedDeviationItem?.id
        );

        return {
          ...prevState,
          deviation: {
            ...prevState.deviation,
            items: nextItems,
            latest_explanation_item:
              prevState.deviation?.latest_explanation_item?.id ===
              selectedDeviationItem?.id
                ? null
                : prevState.deviation?.latest_explanation_item,
            consequence_items: (prevState.deviation?.consequence_items || []).filter(
              (item) => item.id !== selectedDeviationItem?.id
            ),
          },
        };
      });
    }

    setDeviationItemDeletePromptOpen(false);
    setSelectedDeviationItem(null);
  };

  const { deviation, isLoading, error, errors } = itemState;

  const workOrderPickFilters = useMemo(
    () =>
      deviation?.id
        ? [
            {
              name: "without_deviation",
              value: true,
              except_deviation_id: deviation.id,
            },
          ]
        : [],
    [deviation?.id]
  );

  useEffect(() => {
    if (workOrderDialogOpen) setWorkOrderPickerTab("existing");
  }, [workOrderDialogOpen]);

  const goCreateWorkOrderForDeviation = useCallback(() => {
    if (!deviation?.id) return;
    setWorkOrderDialogOpen(false);
    navigate("/dashboard/work_orders/new", {
      state: {
        project: deviation.project,
        customer: deviation.customer,
        delivery_address:
          deviation.project?.delivery_address ||
          deviation.customer?.delivery_address ||
          null,
        return_after_work_order_create: { deviation_id: deviation.id },
        work_order_title_from_deviation: {
          number: deviation.number,
          displayTitle: deviation.displayTitle,
          title: deviation.title,
        },
      },
    });
  }, [deviation, navigate]);

  const explanationItem = deviation?.latest_explanation_item;
  const consequenceItems = useMemo(() => {
    const fromDedicatedField = deviation?.consequence_items || [];
    if (fromDedicatedField.length > 0) return fromDedicatedField;
    return (deviation?.items || []).filter(
      (item) => item.title === "CONSEQUENCE" || item.consequence
    );
  }, [deviation]);

  const summaryItems = [
    {
      label: "Prosjekt",
      value: deviation?.project?.displayTitle || "-",
      path: deviation?.project?.id
        ? `/dashboard/projects/${deviation.project.id}`
        : null,
    },
    {
      label: "Kunde",
      value: deviation?.customer?.displayTitle || "-",
    },
    {
      label: "Ansvarlig",
      value: deviation?.responsible_user?.displayTitle || "-",
    },
    {
      label: "Arbeidsordre",
      value: deviation?.work_order?.displayTitle || "Ikke knyttet",
      path: deviation?.work_order?.id
        ? `/dashboard/work_orders/${deviation.work_order.id}`
        : null,
    },
  ];

  const headerMeta = [
    {
      component: <DeviationSeverity severity={deviation?.severity} />,
      label: "Severity",
      hidden: !deviation?.severity,
    },
    {
      component: <DeviationPriority priority={deviation?.priority} />,
      label: "Priority",
      hidden: !deviation?.priority,
    },
    {
      component: <DeviationStatus deviation={deviation} />,
      label: "Status",
      hidden: false,
    },
  ];

  const headerActions = [
    {
      label: "Rediger",
      action: () => setDeviationDialogOpen(true),
      icon: PencilIcon,
      hidden: !deviation?.id,
      menu: false,
    },
    {
      label: "Send",
      action: () => setSendDialogOpen(true),
      icon: PaperAirplaneIcon,
      hidden: !deviation?.id,
      menu: false,
    },
    {
      label: "Skriv ut",
      action: () => setPrintDialogOpen(true),
      icon: PrinterIcon,
      hidden: !deviation?.id,
      menu: false,
    },
  ];

  if (isLoading) {
    return (
      <DashboardHeader variant="v2" title="Avvik">
        <ResourceLoading />
      </DashboardHeader>
    );
  }

  if (error) {
    return (
      <DashboardHeader variant="v2" title="Avvik">
        <ResourceError errors={errors} />
      </DashboardHeader>
    );
  }

  if (!deviation?.id) {
    return (
      <DashboardHeader variant="v2" title="Avvik">
        <ResourceEmpty />
      </DashboardHeader>
    );
  }

  return (
    <>
      <DashboardHeader
        variant="v2"
        title={deviation?.title || deviation?.displayTitle || "Avvik"}
        subtitle={
          deviation?.displayTitle ? `Avvik / ${deviation.displayTitle}` : "Avvik"
        }
        meta={headerMeta}
        actions={headerActions}
        summaryItems={summaryItems}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 pb-20 sm:px-6 lg:max-w-7xl lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {deviation?.checklist_item?.checklist?.id && (
              <Link
                to={`/dashboard/quality/checklists/${deviation.checklist_item.checklist.id}`}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-3 text-sm font-medium text-white hover:bg-blue-600"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Tilbake til sjekkliste
              </Link>
            )}

            <WorkOrderLinkCard
              deviation={deviation}
              onOpenWorkOrderDialog={() => setWorkOrderDialogOpen(true)}
              t={t}
            />

            <NotificationStatusCard
              notificationStatuses={deviation?.notification_statuses}
            />

            <SectionCard
              title="Forklaring"
              description="Forklaring på avviket eller endringen."
              action={
                <button
                  type="button"
                  onClick={() => setExplanationDialogOpen(true)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {explanationItem ? "Rediger forklaring" : "Legg til forklaring"}
                </button>
              }
            >
              {explanationItem ? (
                <div className="space-y-4">
                  <div className="prose prose-sm max-w-none text-gray-700">
                    <HtmlTextFormatter string={explanationItem.description} />
                  </div>
                  <DocumentViewer
                    pathType="quickview"
                    documents={explanationItem.documents}
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Ingen forklaring er lagt til ennå.
                </p>
              )}
            </SectionCard>

            <SectionCard
              title="Konsekvens"
              description="Konsekvenser som er registrert på avviket."
              action={
                <button
                  type="button"
                  onClick={() => setConsequenceDialogOpen(true)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Legg til konsekvens
                </button>
              }
            >
              {consequenceItems.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Ingen konsekvenser er registrert ennå.
                </p>
              ) : (
                <div className="space-y-4">
                  {consequenceItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <DeviationItemActionStatus item={item} />
                          <p className="text-sm text-gray-700">
                            {item.description}
                          </p>
                          {item.consequence && (
                            <p className="text-xs text-gray-500">
                              {deviationConsequenceOptions[item.consequence - 1]
                                ?.label || ""}
                              {item.created_at
                                ? ` · ${dateToString(item.created_at)}`
                                : ""}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => promptDeleteDeviationItem(item)}
                          className="rounded-md border border-red-200 bg-white p-2 text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-4">
                        <DocumentViewer
                          pathType="quickview"
                          documents={item.documents}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <div className="space-y-6 lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <HistoryModuleView module="deviation" module_param={deviation.id} />
            </div>
          </div>
        </div>
      </DashboardHeader>

      <DeviationNewDialog
        open={deviationDialogOpen}
        onClose={() => setDeviationDialogOpen(false)}
        selected={deviation}
        onRefresh={handleOnRefresh}
      />

      <DeviationPrintDialog
        deviation={deviation}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />

      <DeviationSendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        deviation={deviation}
        onSent={handleOnRefresh}
      />

      <PMDialog
        open={workOrderDialogOpen}
        onClose={() => setWorkOrderDialogOpen(false)}
        title="deviation.work_order.link_dialog_title"
        closeOnOutsideClick
        size="lg"
      >
        {deviation?.id ? (
          <div className="space-y-4">
            <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                onClick={() => setWorkOrderPickerTab("existing")}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  workOrderPickerTab === "existing"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("deviation.work_order.mode_existing")}
              </button>
              <button
                type="button"
                onClick={() => setWorkOrderPickerTab("create")}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  workOrderPickerTab === "create"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("deviation.work_order.mode_create")}
              </button>
            </div>

            {workOrderPickerTab === "existing" ? (
              <Formik
                enableReinitialize
                initialValues={{
                  work_order_id: deviation.work_order?.id ?? null,
                }}
                onSubmit={async (values, helpers) => {
                  helpers.setSubmitting(true);
                  try {
                    const res = await updateDeviation({
                      id: deviation.id,
                      work_order_id: values.work_order_id ?? null,
                    });

                    const apiErrors =
                      res?.errors ||
                      res?.response?.data?.errors ||
                      res?.response?.data?.messages;

                    if (apiErrors) {
                      const msg = Array.isArray(apiErrors)
                        ? apiErrors.join(", ")
                        : typeof apiErrors === "object"
                          ? Object.entries(apiErrors)
                              .flatMap(([k, v]) =>
                                Array.isArray(v)
                                  ? v.map((x) => `${k}: ${x}`)
                                  : [`${k}: ${v}`]
                              )
                              .join(", ")
                          : String(apiErrors);
                      toast.error(msg || t("Could not save"));
                      return;
                    }

                    if (res?.id) {
                      toast.success(t("deviation.work_order.toast_saved"));
                      handleOnRefresh(res);
                      setWorkOrderDialogOpen(false);
                    } else {
                      toast.error(t("Something went wrong..."));
                    }
                  } catch (_err) {
                    toast.error(t("Something went wrong..."));
                  } finally {
                    helpers.setSubmitting(false);
                  }
                }}
              >
                {(formik) => (
                  <Form className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {t("deviation.work_order.existing_intro")}
                    </p>
                    <WorkOrderSelector
                      name="work_order_id"
                      label={t("Work Order")}
                      selected={deviation.work_order ?? null}
                      project={deviation.project}
                      extraFilters={workOrderPickFilters}
                      {...formik}
                    />
                    <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                      <button
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                      >
                        {t("deviation.work_order.link_submit")}
                      </button>
                      {formik.values.work_order_id ? (
                        <button
                          type="button"
                          disabled={formik.isSubmitting}
                          onClick={() =>
                            formik.setFieldValue("work_order_id", null)
                          }
                          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                          {t("deviation.work_order.unlink")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={formik.isSubmitting}
                        onClick={() => setWorkOrderDialogOpen(false)}
                        className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {t("deviation.work_order.create_intro")}
                </p>
                <button
                  type="button"
                  onClick={goCreateWorkOrderForDeviation}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  <PlusIcon className="h-5 w-5 shrink-0" />
                  {t("deviation.work_order.create_button")}
                </button>
                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setWorkOrderDialogOpen(false)}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    {t("Cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </PMDialog>

      <DeviationItemEditorDialog
        open={explanationDialogOpen}
        onClose={() => setExplanationDialogOpen(false)}
        deviation={deviation}
        item={explanationItem}
        kind="explanation"
        onSaved={syncSavedItemIntoState}
      />

      <DeviationItemEditorDialog
        open={consequenceDialogOpen}
        onClose={() => setConsequenceDialogOpen(false)}
        deviation={deviation}
        kind="consequence"
        onSaved={syncSavedItemIntoState}
      />

      <SimplePrompt
        title="Slett innlegg"
        description="Er du sikker på at du ønsker å slette innlegget?"
        onAccept={handleDeleteDeviationItem}
        open={deviationItemDeletePromptOpen}
        onClose={() => setDeviationItemDeletePromptOpen(false)}
      />
    </>
  );
};
